import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Pager } from "../components/Pager";
import { Card, Chips, ErrorBanner, Pill, fmt, recipientsLabel, usePaged } from "../components/ui";
import { api, type Blast, type BlastProgress } from "../lib/api";

type Filter = "all" | "sending" | "waiting" | "done" | "stopped";

// Going out now, or stopped part-way - the only ones worth a full card and a
// five-second poll. A blast built and never sent sits at "ready" for ever, and
// staging had dozens: as cards they buried the one actually running.
const SENDING = ["running", "paused"];
const sending = (b: Blast) => SENDING.includes(b.status);
// Built or booked, waiting for somebody or for its time.
const waiting = (b: Blast) => ["ready", "scheduled", "building", "draft"].includes(b.status);
const live = (b: Blast) => sending(b) || waiting(b);
const stopped = (b: Blast) => ["canceled", "failed"].includes(b.status);

/** Blasts: what is sending now, in full; everything else as one row each.
 *
 *  Every blast used to be a full card, newest first, so the screen grew by a
 *  card per send and the one running now was wherever it happened to fall.
 *  The ones sending stay on top as cards - they have buttons someone may need in
 *  a hurry - and the rest are a table you can search, filter and page, with
 *  each row opening to the same detail the card showed.
 */
export function Blasts() {
  const [blasts, setBlasts] = useState<Blast[]>([]);
  const [progress, setProgress] = useState<Record<number, BlastProgress>>({});
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<Set<number>>(new Set());
  const asked = useRef<Set<number>>(new Set());
  const inFlight = useRef(false);
  //: Which poll we are on, so finished blasts are refreshed occasionally
  //: rather than every five seconds or never again.
  const rounds = useRef(0);
  //: The rows on screen. Progress is an aggregate over the queue per blast, so
  //: it is asked only for blasts somebody can see - never for page 12.
  const visible = useRef<number[]>([]);

  const newestFirst = useMemo(() => [...blasts].sort((a, b) => b.id - a.id), [blasts]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return newestFirst.filter(
      (b) =>
        (filter === "all" ||
          (filter === "sending" && sending(b)) ||
          (filter === "waiting" && waiting(b)) ||
          (filter === "done" && b.status === "completed") ||
          (filter === "stopped" && stopped(b))) &&
        (!needle || b.name.toLowerCase().includes(needle)),
    );
  }, [newestFirst, q, filter]);
  const paged = usePaged(filtered, [q, filter]);
  visible.current = paged.shown.map((b) => b.id);

  const load = () => {
    // One round at a time: each round is a queue aggregate per blast asked,
    // and firing another before the last has landed multiplies them.
    if (inFlight.current) return Promise.resolve();
    inFlight.current = true;
    return api
      .blasts()
      .then(async (bs) => {
        setBlasts(bs);
        // The counters on the blast row are written in exactly one place -
        // when a blast COMPLETES - so everything true about a blast is counted
        // off the queue, by /progress. Live blasts every round; a visible
        // finished one the first time it is seen and then once a minute,
        // because replies keep arriving for days.
        rounds.current += 1;
        const slowRound = rounds.current % 12 === 1;
        const onScreen = new Set(visible.current);
        const want = bs.filter(
          (b) =>
            sending(b) || (onScreen.has(b.id) && (slowRound || !asked.current.has(b.id))),
        );
        if (want.length === 0) return;
        const ps = await Promise.all(want.map((b) => api.blastProgress(b.id).catch(() => null)));
        for (const p of ps) if (p) asked.current.add(p.id);
        setProgress((prev) => ({
          ...prev,
          ...Object.fromEntries(ps.filter(Boolean).map((p) => [p!.id, p!])),
        }));
      })
      .catch((e) => setError(e.message))
      .finally(() => {
        inFlight.current = false;
      });
  };

  // Poll rather than hold a connection: a websocket would drop on every
  // deploy and every instance recycle. Paused while the tab is hidden, and
  // reloaded the instant it is looked at again.
  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 5000);
    const wake = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", wake);
    };
  }, []);

  // A new page of rows is asked for straight away rather than on the next tick.
  useEffect(() => {
    if (paged.shown.some((b) => !asked.current.has(b.id))) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged.page, paged.perPage, q, filter, blasts.length]);

  const act = (p: Promise<unknown>) => p.then(load).catch((e) => setError(e.message));
  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const nowSending = newestFirst.filter(sending);
  const readyCount = blasts.filter(waiting).length;
  const count = (f: (b: Blast) => boolean) => blasts.filter(f).length;

  return (
    <>
      <ErrorBanner error={error} />
      <div className="row">
        <h2 style={{ fontSize: 19 }}>Blasts</h2>
        <div className="sp" />
        <a className="btn p" href="/blasts/new">
          New blast
        </a>
      </div>

      {blasts.length === 0 && <div className="empty">No blasts yet.</div>}

      {nowSending.map((b) => (
        <Card
          key={b.id}
          title={<BlastTitle b={b} />}
          actions={<BlastActions b={b} p={progress[b.id]} act={act} />}
          footer={<HeldBack b={b} />}
        >
          <BlastDetail b={b} p={progress[b.id]} />
        </Card>
      ))}

      {readyCount > 0 && filter !== "waiting" && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          {fmt.n(readyCount)} blast{readyCount === 1 ? " is" : "s are"} built and not sent yet.
          <button className="btn s" onClick={() => setFilter("waiting")}>
            Show {readyCount === 1 ? "it" : "them"}
          </button>
        </div>
      )}

      {blasts.length > 0 && (
        <Card
          title="All blasts"
          footer={
            <span className="dim">
              Replied counts people, not messages: a reply belongs to the last text that person
              was sent, and keeps counting after the blast finishes. Delivery receipts are not
              reported back by the gateway, so there is no delivered column.
            </span>
          }
        >
          <div className="row w" style={{ marginBottom: 12 }}>
            <input
              className="f"
              style={{ width: 240 }}
              placeholder="Search by name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Chips<Filter>
              value={filter}
              onChange={setFilter}
              options={[
                { key: "all", label: "All", count: blasts.length },
                { key: "sending", label: "Sending now", count: count(sending) },
                { key: "waiting", label: "Ready or scheduled", count: count(waiting) },
                { key: "done", label: "Finished", count: count((b) => b.status === "completed") },
                { key: "stopped", label: "Stopped", count: count(stopped) },
              ]}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="empty">No blasts match.</div>
          ) : (
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Blast</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th style={{ textAlign: "right" }}>Recipients</th>
                    <th style={{ textAlign: "right" }}>Sent</th>
                    <th style={{ textAlign: "right" }}>Failed</th>
                    <th style={{ textAlign: "right" }}>Replied</th>
                    <th style={{ textAlign: "right" }}>Reply rate</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.shown.map((b) => {
                    const p = progress[b.id];
                    const sent = p?.sent ?? b.count_sent;
                    const replied = p?.replied ?? b.count_replied;
                    const isOpen = open.has(b.id);
                    return (
                      <Fragment key={b.id}>
                        <tr onClick={() => toggle(b.id)} style={{ cursor: "pointer" }}>
                          <td style={{ fontWeight: 600 }}>
                            <span className="dim" style={{ marginRight: 6 }}>
                              {isOpen ? "▾" : "▸"}
                            </span>
                            {b.name}
                            {b.personalize && (
                              <span style={{ marginLeft: 7 }}>
                                <Pill tone="ok">customized</Pill>
                              </span>
                            )}
                            {b.build_at_send && (
                              <span style={{ marginLeft: 7 }}>
                                <Pill tone="ac">list built at send</Pill>
                              </span>
                            )}
                            {b.standby && (
                              <span style={{ marginLeft: 7 }}>
                                <Pill tone="nu">standby - when the team has room</Pill>
                              </span>
                            )}
                          </td>
                          <td>
                            <StatusPill status={b.status} />
                          </td>
                          <td className="dim" style={{ whiteSpace: "nowrap" }}>
                            {fmt.et(b.started_at ?? b.scheduled_at)}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {b.build_at_send && b.total_recipients === 0 ? (
                              <span className="dim" title="The list is built at the send time">
                                {recipientsLabel(b)}
                              </span>
                            ) : (
                              fmt.n(b.total_recipients)
                            )}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {fmt.n(sent)}
                          </td>
                          <td
                            className="mono"
                            style={{ textAlign: "right", color: (p?.failed ?? b.count_failed) > 0 ? "var(--bad)" : undefined }}
                          >
                            {fmt.n(p?.failed ?? b.count_failed)}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {fmt.n(replied)}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {sent > 0 ? fmt.pct((100 * replied) / sent) : "—"}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={8} style={{ background: "var(--surf2)" }}>
                              <div className="row" style={{ marginBottom: 10 }}>
                                <HeldBack b={b} />
                                <div className="sp" />
                                <BlastActions b={b} p={p} act={act} />
                              </div>
                              <BlastDetail b={b} p={p} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Pager
            page={paged.page}
            pages={paged.pages}
            total={paged.total}
            perPage={paged.perPage}
            onPage={paged.setPage}
            onPerPage={paged.setPerPage}
          />
        </Card>
      )}
    </>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "running" ? "ok" : ["canceled", "failed"].includes(status) ? "ba" : status === "completed" ? "nu" : "wa";
  return <Pill tone={tone}>{status === "canceled" ? "stopped" : status}</Pill>;
}

function BlastTitle({ b }: { b: Blast }) {
  return (
    <div className="row" style={{ gap: 9 }}>
      <h3>{b.name}</h3>
      <StatusPill status={b.status} />
      {/* Two blasts to the same people on the same day look identical
          without this - one wrote 849 messages one at a time and cost real
          money, the other sent one line 879 times. */}
      {b.personalize && <Pill tone="ok">customized</Pill>}
      {b.build_at_send && <Pill tone="ac">list built at send</Pill>}
    </div>
  );
}

function BlastActions({
  b,
  p,
  act,
}: {
  b: Blast;
  p?: BlastProgress;
  act: (p: Promise<unknown>) => void;
}) {
  const due = (x: Blast) => !!x.scheduled_at && new Date(x.scheduled_at).getTime() <= Date.now();
  const sent = p?.sent ?? b.count_sent;
  const left = p?.remaining ?? Math.max(0, b.total_recipients - sent);
  return (
    <div className="row" style={{ gap: 7 }} onClick={(e) => e.stopPropagation()}>
      {b.status === "ready" && b.build_at_send && (
        <button
          className="btn s p"
          onClick={() => {
            // Arming it: the list is built from whoever matches at the send
            // time, so the person is agreeing to a time and a rule, not a count.
            // Once that time has passed, pressing it builds the list and starts
            // sending straight away - which is how a ready-made lever is used -
            // so it says so rather than quoting a time already gone.
            const msg = due(b)
              ? `Five9 will build the list now from everyone who matches the audience, ` +
                `and start sending at ${b.rate_per_minute} a minute.

Send it now?`
              : `At ${fmt.et(b.scheduled_at)} Five9 will build the list from everyone ` +
                `who matches the audience then, and send it.

Schedule it?`;
            if (confirm(msg)) act(api.launchBlast(b.id));
          }}
        >
          {due(b) ? "Send it now" : "Schedule it"}
        </button>
      )}
      {b.status === "ready" && !b.build_at_send && (
        <button className="btn s p" onClick={() => act(api.launchBlast(b.id))}>
          Send it
        </button>
      )}
      {b.status === "running" && (
        <button className="btn s" onClick={() => act(api.pauseBlast(b.id))}>
          Pause
        </button>
      )}
      {b.status === "paused" && (
        <button
          className="btn s p"
          onClick={() => {
            // Pause holds the rest of the queue rather than dropping it, so
            // resuming sends to people who have been waiting - say how many,
            // and at what rate, before it starts again.
            const msg =
              `${fmt.n(left)} messages are held and will start sending again ` +
              `at ${b.rate_per_minute} a minute.\n\nResume?`;
            if (confirm(msg)) act(api.resumeBlast(b.id));
          }}
        >
          Resume
        </button>
      )}
      {["running", "paused", "ready", "scheduled"].includes(b.status) && (
        <button
          className="btn s d"
          onClick={() => {
            // The dialog states the honest position: already-sent messages
            // cannot be recalled, only the rest stopped.
            const msg =
              `${fmt.n(sent)} messages have already been sent and cannot be recalled.` +
              `\n\nCancel the remaining ${fmt.n(left)}?`;
            if (confirm(msg)) act(api.cancelBlast(b.id));
          }}
        >
          Stop
        </button>
      )}
    </div>
  );
}

function HeldBack({ b }: { b: Blast }) {
  if (!b.exclusion_breakdown || Object.keys(b.exclusion_breakdown).length === 0) return null;
  return (
    <span className="dim" style={{ fontSize: 12 }}>
      Held back:{" "}
      {Object.entries(b.exclusion_breakdown)
        .map(([k, v]) => `${fmt.n(v)} ${k.replace(/_/g, " ")}`)
        .join(" · ")}
    </span>
  );
}

function BlastDetail({ b, p }: { b: Blast; p?: BlastProgress }) {
  const sent = p?.sent ?? b.count_sent;
  const failed = p?.failed ?? b.count_failed;
  // People who answered this blast, not the messages they sent - someone who
  // writes eleven times is one reply.
  const replied = p?.replied ?? b.count_replied;

  return (
    <>
      <div className="tiles">
        <div className="tile">
          <span className="lab">Recipients</span>
          <span className="v">{recipientsLabel(b)}</span>
          {b.build_at_send && b.total_recipients === 0 && b.status !== "canceled" && (
            <span className="d">match right now · the list is built from whoever matches when it sends</span>
          )}
        </div>
        <div className="tile">
          <span className="lab">Sent</span>
          <span className="v">{fmt.n(sent)}</span>
        </div>
        <div className={`tile ${failed > 0 ? "ba" : ""}`}>
          <span className="lab">Failed</span>
          <span className="v">{fmt.n(failed)}</span>
        </div>
        {p && p.suppressed > 0 && (
          <div className="tile">
            <span className="lab">Held at send</span>
            <span className="v">{fmt.n(p.suppressed)}</span>
            <span className="d">opted out or capped since the list was built</span>
          </div>
        )}
        {replied > 0 && (
          <div className="tile ok">
            <span className="lab">Replied</span>
            <span className="v">{fmt.n(replied)}</span>
            {p?.reply_messages != null && p.reply_messages !== replied && (
              <span className="d">{fmt.n(p.reply_messages)} messages</span>
            )}
          </div>
        )}
      </div>

      {p && p.total > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{ height: 9, borderRadius: 6, background: "var(--surf3)", overflow: "hidden" }}
          >
            <div style={{ width: `${p.percent}%`, height: "100%", background: "var(--acc)" }} />
          </div>
          <div className="mono dim" style={{ fontSize: 11, marginTop: 6 }}>
            {/* A stopped blast is 100% "finished" only because the rest were
                cancelled, so saying so beats a percentage that reads like
                everything went out. */}
            {live(b)
              ? `${fmt.pct(p.percent)} · ${fmt.n(p.remaining)} still to go`
              : `${fmt.n(p.sent)} sent · ${fmt.n(p.failed)} failed${
                  p.canceled ? ` · ${fmt.n(p.canceled)} stopped before sending` : ""
                }`}
          </div>

          {/* How the personalisation is actually going. This failure is
              SILENT without it: a draft that does not arrive in time sends
              the template instead, which is a real message, so nothing errors
              and nobody finds out the customised blast was not customised. */}
          {p.personalized && (
            <div className="mono" style={{ fontSize: 11, marginTop: 4 }}>
              <span style={{ color: "var(--acc)" }}>{fmt.n(p.drafts_written)} written for them</span>
              {p.drafts_fell_back > 0 && (
                <span className="dim"> · {fmt.n(p.drafts_fell_back)} sent as the template</span>
              )}
              {p.drafts_waiting > 0 && (
                <span className="dim"> · {fmt.n(p.drafts_waiting)} still writing</span>
              )}
              <span className="dim"> · ${p.draft_cost_usd.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
